import React from "react";
import { useMutation } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { Alert, Button, Chip, Stack, Typography, useTheme } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ChatsCircle,
  IdentificationCard,
  Password,
  ReadCvLogo,
  Signature,
  UserCircle,
} from "@phosphor-icons/react";

import * as selectors from "selectors";
import actions from "actions";
import { startIntakeFlow } from "api/customers";
import useCompleteIntake from "hooks/api/customers/useCompleteIntake";
import { useIntake } from "components/SelfService/IntakeProvider";
import { STAGES } from "components/SelfService/utils";
import { useGlobalToast } from "components/GlobalToastProvider";
import { formatPhoneNumber } from "utils/formatPhoneNumber";
import SummaryTaskItem from "./SummaryTaskItem";

export default function Summary({ hasStartedIntake }) {
  const theme = useTheme();
  const history = useHistory();
  const { showToast } = useGlobalToast();
  const dispatch = useDispatch();
  const customerDetails = useSelector(selectors.getCustomerDetails);
  const { details } = customerDetails;
  const { intakeFlowJson, intakeFlow, clientId } = useIntake();

  const { completeIntakeMutation, isIntakeMutationLoading } = useCompleteIntake({
    onSuccess: () => {
      showToast({ message: "Intake successfully completed" });
      dispatch(actions.loadCustomerDetails(clientId));
    },
  });

  const { mutate: startIntake, isLoading: startIntakeLoading } = useMutation(
    () => startIntakeFlow(clientId),
    {
      onSuccess: () => {
        showToast({ message: "Success!" });
        dispatch(actions.loadCustomerDetails(clientId));
      },
      onError: () => {
        showToast({
          message: "Whoops! Something went wrong",
          errorState: true,
          retryHandler: startIntake,
        });
      },
    }
  );

  const isReadyToComplete =
    !!intakeFlowJson?.password_created_date &&
    !!intakeFlowJson?.profileInformation?.isComplete &&
    !!intakeFlowJson?.insuranceCard?.isComplete &&
    !!intakeFlowJson?.diagnosisReport?.isComplete;

  const showInvitationAlert = hasStartedIntake && !intakeFlowJson?.password_created_date;
  const invitationDate = intakeFlow?.created_at
    ? format(new Date(intakeFlow.created_at), "MMM d, yyyy")
    : null;

  const lastVisitedDate = intakeFlowJson?.lastVisitedDate
    ? new Date(intakeFlowJson.lastVisitedDate)
    : null;

  const getLastVisitedText = () => {
    if (
      intakeFlowJson?.lastVisitedStage &&
      STAGES[intakeFlowJson.lastVisitedStage] &&
      lastVisitedDate instanceof Date &&
      !isNaN(lastVisitedDate)
    ) {
      return `${STAGES[intakeFlowJson.lastVisitedStage].displayName} on ${format(lastVisitedDate, "do MMMM yyyy h:mm a")}`;
    }
    if (intakeFlowJson?.password_created_date) {
      return `Setting a Password on ${format(new Date(intakeFlowJson.password_created_date), "do MMMM yyyy h:mm a")}`;
    }
    return "-";
  };

  const lastVisitedText = getLastVisitedText();

  const designatedCaregiver =
    details?.first_name && details?.last_name
      ? `${details.first_name} ${details.last_name}`
      : "---";

  const handleOpenMessages = () => {
    history.push(`/messages?chat_id=${details?.id}`);
  };

  return (
    <Stack gap={theme.spacing(7)}>
      {/* Account Snapshot */}
      <Stack bgcolor="grey.50" borderRadius={4} p={3} pt={5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" px={3} mb={5}>
          <Stack direction="row" alignItems="center" gap={2}>
            <Typography variant="h4">Account Snapshot</Typography>
            <Chip label="Intake" color="warning" size="small" variant="outlined" />
          </Stack>
          <Button
            color="secondary"
            size="small"
            startIcon={<ChatsCircle weight="duotone" />}
            onClick={handleOpenMessages}
          >
            Open Messages
          </Button>
        </Stack>

        <Stack bgcolor="background.paper" borderRadius={4} p={7} border={1} borderColor="grey.200">
          <Stack gap={"24px"}>
            <Stack gap={3}>
              <Typography variant="inputLabel" fontWeight={500} color="grey.600">
                Designated Caregiver
              </Typography>
              <Typography variant="body2" color="text.primary">
                {designatedCaregiver}
              </Typography>
            </Stack>
            <Stack gap={3}>
              <Typography variant="inputLabel" fontWeight={500} color="grey.600">
                Phone
              </Typography>
              <Typography variant="body2" color="text.primary">
                {details?.phone_number ? formatPhoneNumber(details.phone_number) : "---"}
              </Typography>
            </Stack>
            <Stack gap={3}>
              <Typography variant="inputLabel" fontWeight={500} color="grey.600">
                Email Address
              </Typography>
              <Typography variant="body2" color="text.primary">
                {details?.email || "---"}
              </Typography>
            </Stack>
            <Stack gap={3}>
              <Typography variant="inputLabel" fontWeight={500} color="grey.600">
                Last Step Visited
              </Typography>
              <Typography variant="body2" color="text.primary">
                {lastVisitedText}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {/* Onboarding Status */}
      {hasStartedIntake && (
        <Stack bgcolor="grey.50" borderRadius={4} p={3} pt={5}>
          <Stack px={3} mb={5}>
            <Typography variant="h4">Onboarding Status</Typography>
            <Typography variant="body2" color="text.secondary">
              See where {details?.name} {details?.child_last_name} is in the Intake process and
              complete any outstanding steps on their behalf if needed.
            </Typography>
          </Stack>

          <Stack
            bgcolor="background.paper"
            borderRadius={4}
            p={3}
            gap={"2px"}
            border={1}
            borderColor="grey.200"
          >
            {showInvitationAlert && invitationDate && (
              <Alert severity="warning" style={{ marginBottom: 16, borderRadius: 16 }}>
                Invitation sent on {invitationDate}.
              </Alert>
            )}
            <SummaryTaskItem
              Icon={Password}
              title="Setting a Password"
              isComplete={!!intakeFlowJson?.password_created_date}
              completedDate={intakeFlowJson?.password_created_date}
            />
            <SummaryTaskItem
              Icon={UserCircle}
              IconProps={{ weight: "duotone" }}
              title="Confirm account details"
              isComplete={!!intakeFlowJson?.profileInformation?.isComplete}
              completedDate={intakeFlowJson?.profileInformation?.completedDate}
            />
            <SummaryTaskItem
              Icon={Signature}
              title="Pediatrician Release of Information"
              isComplete={
                intakeFlowJson?.pediatricianROI?.isComplete ??
                !!intakeFlowJson?.forms_completed_date
              }
              completedDate={
                intakeFlowJson?.pediatricianROI?.completedDate ??
                intakeFlowJson?.forms_completed_date
              }
            />
            <SummaryTaskItem
              Icon={Signature}
              title="Diagnosing Doctor Release of Information"
              isComplete={
                intakeFlowJson?.diagnosingDoctorROI?.isComplete ??
                !!intakeFlowJson?.forms_completed_date
              }
              completedDate={
                intakeFlowJson?.diagnosingDoctorROI?.completedDate ??
                intakeFlowJson?.forms_completed_date
              }
            />
            <SummaryTaskItem
              Icon={Signature}
              title="Behavioral Health Services Consent"
              isComplete={
                intakeFlowJson?.behavioralHealthConsent?.isComplete ??
                !!intakeFlowJson?.forms_completed_date
              }
              completedDate={
                intakeFlowJson?.behavioralHealthConsent?.completedDate ??
                intakeFlowJson?.forms_completed_date
              }
            />
            <SummaryTaskItem
              Icon={Signature}
              title="Research Consent"
              isComplete={
                intakeFlowJson?.researchConsent?.isComplete ??
                !!intakeFlowJson?.forms_completed_date
              }
              completedDate={
                intakeFlowJson?.researchConsent?.completedDate ??
                intakeFlowJson?.forms_completed_date
              }
            />
            <SummaryTaskItem
              Icon={IdentificationCard}
              IconProps={{ weight: "duotone" }}
              title="Insurance Cards"
              isComplete={!!intakeFlowJson?.insuranceCard?.isComplete}
              completedDate={intakeFlowJson?.insuranceCard?.completedDate}
            />
            <SummaryTaskItem
              Icon={ReadCvLogo}
              IconProps={{ weight: "duotone" }}
              title="Diagnostic Report"
              isComplete={!!intakeFlowJson?.diagnosisReport?.isComplete}
              completedDate={intakeFlowJson?.diagnosisReport?.completedDate}
            />
          </Stack>
        </Stack>
      )}

      {/* Action Buttons */}
      <Stack>
        {hasStartedIntake ? (
          <LoadingButton
            loading={isIntakeMutationLoading}
            disabled={!isReadyToComplete}
            onClick={() => completeIntakeMutation(clientId)}
          >
            Finish Intake
          </LoadingButton>
        ) : (
          <LoadingButton onClick={startIntake} loading={startIntakeLoading}>
            Send Invite and Start Intake
          </LoadingButton>
        )}
      </Stack>
    </Stack>
  );
}
